/**
 * useCreators — the global UAE creator (influencer) directory.
 *
 * A `creators` row is one real person, independent of any Community Goal.
 * Goal-scoped `discovery_tastemakers` rows point at a creator via `creator_id`,
 * so the same influencer can be featured on many trails without duplicating.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const CREATOR_EMIRATES = [
  "Abu Dhabi",
  "Dubai",
  "Sharjah",
  "Ajman",
  "Umm Al Quwain",
  "Ras Al Khaimah",
  "Fujairah",
] as const;

export const CREATOR_CATEGORIES = [
  "Food",
  "Family",
  "Outdoors",
  "Luxury",
  "Fitness",
  "Culture",
  "Travel",
  "Lifestyle",
] as const;

export const FOLLOWER_BANDS = [
  { value: "unknown", label: "Prefer not to say" },
  { value: "under_1k", label: "Under 1k" },
  { value: "1k_10k", label: "1k – 10k" },
  { value: "10k_50k", label: "10k – 50k" },
  { value: "50k_plus", label: "50k+" },
] as const;

export type CreatorStatus = "pending" | "verified" | "rejected";

export interface Creator {
  id: string;
  userId: string | null;
  displayName: string;
  handle: string | null;
  avatarUrl: string | null;
  bio: string | null;
  instagramUrl: string | null;
  tiktokUrl: string | null;
  youtubeUrl: string | null;
  followerBand: string;
  emirates: string[];
  categories: string[];
  status: CreatorStatus;
  adminNotes: string | null;
  createdAt: string;
  /** One-time VIP invite token (admin-visible only). Null once claimed. */
  vipToken: string | null;
}

export function mapCreator(r: any): Creator {
  return {
    id: r.id,
    userId: r.user_id ?? null,
    displayName: r.display_name,
    handle: r.handle ?? null,
    avatarUrl: r.avatar_url ?? null,
    bio: r.bio ?? null,
    instagramUrl: r.instagram_url ?? null,
    tiktokUrl: r.tiktok_url ?? null,
    youtubeUrl: r.youtube_url ?? null,
    followerBand: r.follower_band ?? "unknown",
    emirates: r.emirates ?? [],
    categories: r.categories ?? [],
    status: (r.status ?? "pending") as CreatorStatus,
    adminNotes: r.admin_notes ?? null,
    createdAt: r.created_at,
    vipToken: r.vip_token ?? null,
  };
}

export interface CreatorInput {
  displayName: string;
  handle?: string | null;
  bio?: string | null;
  instagramUrl?: string | null;
  tiktokUrl?: string | null;
  youtubeUrl?: string | null;
  followerBand?: string;
  emirates?: string[];
  categories?: string[];
}

function toRow(input: CreatorInput) {
  const row: Record<string, unknown> = {};
  if (input.displayName !== undefined) row.display_name = input.displayName.trim();
  if (input.handle !== undefined) row.handle = input.handle?.trim().replace(/^@/, "") || null;
  if (input.bio !== undefined) row.bio = input.bio?.trim() || null;
  if (input.instagramUrl !== undefined) row.instagram_url = input.instagramUrl?.trim() || null;
  if (input.tiktokUrl !== undefined) row.tiktok_url = input.tiktokUrl?.trim() || null;
  if (input.youtubeUrl !== undefined) row.youtube_url = input.youtubeUrl?.trim() || null;
  if (input.followerBand !== undefined) row.follower_band = input.followerBand;
  if (input.emirates !== undefined) row.emirates = input.emirates;
  if (input.categories !== undefined) row.categories = input.categories;
  return row;
}

/* ------------------------------------------------------------------ */
/* The signed-in user's own creator profile                            */
/* ------------------------------------------------------------------ */

export function useMyCreatorProfile() {
  return useQuery({
    queryKey: ["myCreatorProfile"],
    staleTime: 30_000,
    queryFn: async (): Promise<Creator | null> => {
      const { data: userResp } = await supabase.auth.getUser();
      const uid = userResp.user?.id;
      if (!uid) return null;
      const { data, error } = await supabase
        .from("creators" as any)
        .select("*")
        .eq("user_id", uid)
        .maybeSingle();
      if (error) return null;
      return data ? mapCreator(data) : null;
    },
  });
}

/**
 * Admins are self-verified on sign-up so they can see the real creator hub
 * (and everything a verified influencer sees) without waiting in the queue.
 * The server enforces this too: the "users can create their own profile"
 * policy only allows status 'pending', so a non-admin insert asking for
 * 'verified' is rejected outright.
 */
async function statusForNewProfile(): Promise<"pending" | "verified"> {
  try {
    const { data: userResp } = await supabase.auth.getUser();
    const uid = userResp.user?.id;
    if (!uid) return "pending";
    const { data } = await supabase.rpc("has_role" as any, {
      _user_id: uid,
      _role: "admin",
    });
    return data === true ? "verified" : "pending";
  } catch {
    return "pending";
  }
}

/** Create or update the signed-in user's creator profile. */
export function useSaveMyCreatorProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatorInput): Promise<Creator> => {
      const { data: userResp } = await supabase.auth.getUser();
      const uid = userResp.user?.id;
      if (!uid) throw new Error("Please sign in first.");

      const { data: existing } = await supabase
        .from("creators" as any)
        .select("id")
        .eq("user_id", uid)
        .maybeSingle();

      if (existing) {
        const { data, error } = await supabase
          .from("creators" as any)
          .update(toRow(input))
          .eq("id", (existing as any).id)
          .select("*")
          .maybeSingle();
        if (error) throw error;
        return mapCreator(data);
      }

      const status = await statusForNewProfile();
      const { data, error } = await supabase
        .from("creators" as any)
        .insert({ ...toRow(input), user_id: uid, status })
        .select("*")
        .maybeSingle();
      if (error) throw error;
      return mapCreator(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["myCreatorProfile"] });
      qc.invalidateQueries({ queryKey: ["adminCreators"] });
      qc.invalidateQueries({ queryKey: ["searchCreators"] });
    },
  });
}

/**
 * Attach the signed-in user to a creator profile after a stop contribution,
 * so every contribution grows the directory. Silent by design — a failure
 * here must never break the contribution itself.
 */
export async function ensureCreatorProfile(input: CreatorInput): Promise<void> {
  try {
    const { data: userResp } = await supabase.auth.getUser();
    const uid = userResp.user?.id;
    if (!uid || !input.displayName?.trim()) return;
    const { data: existing } = await supabase
      .from("creators" as any)
      .select("id")
      .eq("user_id", uid)
      .maybeSingle();
    if (existing) return;
    await supabase
      .from("creators" as any)
      .insert({ ...toRow(input), user_id: uid, status: "pending" });
  } catch {
    // non-blocking
  }
}

/* ------------------------------------------------------------------ */
/* Public directory (verified only)                                    */
/* ------------------------------------------------------------------ */

export interface DirectoryCreator extends Creator {
  featuredStops: number;
}

export function useSearchCreators(filters: {
  emirate?: string | null;
  category?: string | null;
  enabled?: boolean;
}) {
  const { emirate = null, category = null, enabled = true } = filters;
  return useQuery({
    queryKey: ["searchCreators", emirate, category],
    enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<DirectoryCreator[]> => {
      const { data, error } = await supabase.rpc("search_creators" as any, {
        p_emirate: emirate || null,
        p_category: category || null,
        p_limit: 60,
      });
      if (error) return [];
      return ((data as any[]) || []).map((r) => ({
        ...mapCreator({ ...r, status: "verified" }),
        featuredStops: Number(r.featured_stops ?? 0),
      }));
    },
  });
}

/* ------------------------------------------------------------------ */
/* Public registry: directory listing + profile (verified only)        */
/* ------------------------------------------------------------------ */

export interface PublicCreator extends Creator {
  postsCount: number;
  trailsCount: number;
}

export type CreatorSort = "activity" | "newest";

function mapPublicCreator(r: any): PublicCreator {
  return {
    ...mapCreator({ ...r, status: "verified" }),
    postsCount: Number(r.posts_count ?? 0),
    trailsCount: Number(r.trails_count ?? 0),
  };
}

/** Badges are derived from approved posts — no extra tables. */
export function creatorBadges(postsCount: number): string[] {
  const badges: string[] = [];
  if (postsCount >= 1) badges.push("Contributor");
  if (postsCount >= 5) badges.push("Trail Regular");
  if (postsCount >= 15) badges.push("Top Creator");
  return badges;
}

export function usePublicCreators(filters: {
  emirate?: string | null;
  category?: string | null;
  search?: string;
  sort?: CreatorSort;
}) {
  const { emirate = null, category = null, search = "", sort = "activity" } = filters;
  return useQuery({
    queryKey: ["publicCreators", emirate, category, search.trim(), sort],
    staleTime: 60_000,
    queryFn: async (): Promise<PublicCreator[]> => {
      const { data, error } = await supabase.rpc("list_public_creators" as any, {
        p_emirate: emirate || null,
        p_category: category || null,
        p_search: search.trim() || null,
        p_sort: sort,
        p_limit: 100,
      });
      if (error) throw error;
      return ((data as any[]) || []).map(mapPublicCreator);
    },
  });
}

export function usePublicCreator(handle?: string) {
  return useQuery({
    queryKey: ["publicCreator", handle],
    enabled: !!handle,
    staleTime: 60_000,
    queryFn: async (): Promise<PublicCreator | null> => {
      const { data, error } = await supabase.rpc("get_public_creator" as any, {
        p_handle: handle,
      });
      if (error) throw error;
      const row = ((data as any[]) || [])[0];
      return row ? mapPublicCreator(row) : null;
    },
  });
}

export interface PublicCreatorCredit {
  stopId: string;
  stopName: string;
  emirate: string | null;
  photoUrl: string | null;
  goalId: string | null;
  goalTitle: string | null;
  socialLink: string | null;
  whyILoveIt: string | null;
  recommendedDish: string | null;
}

export function usePublicCreatorCredits(creatorId?: string) {
  return useQuery({
    queryKey: ["publicCreatorCredits", creatorId],
    enabled: !!creatorId,
    staleTime: 60_000,
    queryFn: async (): Promise<PublicCreatorCredit[]> => {
      const { data, error } = await supabase.rpc("get_public_creator_credits" as any, {
        p_creator_id: creatorId,
      });
      if (error) throw error;
      return ((data as any[]) || []).map((r) => ({
        stopId: r.stop_id,
        stopName: r.stop_name ?? "Place",
        emirate: r.stop_emirate ?? null,
        photoUrl: r.stop_photo_url ?? null,
        goalId: r.goal_id ?? null,
        goalTitle: r.goal_title ?? null,
        socialLink: r.social_link ?? null,
        whyILoveIt: r.why_i_love_it ?? null,
        recommendedDish: r.recommended_dish ?? null,
      }));
    },
  });
}

/* ------------------------------------------------------------------ */
/* Admin CRM                                                           */
/* ------------------------------------------------------------------ */

export function useAdminCreators(status?: CreatorStatus | "all") {
  return useQuery({
    queryKey: ["adminCreators", status ?? "all"],
    queryFn: async (): Promise<Creator[]> => {
      let q = supabase
        .from("creators" as any)
        .select("*")
        .order("created_at", { ascending: false })
        .limit(300);
      if (status && status !== "all") q = q.eq("status", status);
      const { data, error } = await q;
      if (error) throw error;
      return (data || []).map(mapCreator);
    },
  });
}

export function useAdminCreatorMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["adminCreators"] });
    qc.invalidateQueries({ queryKey: ["searchCreators"] });
    qc.invalidateQueries({ queryKey: ["myCreatorProfile"] });
  };

  const setStatus = useMutation({
    mutationFn: async ({
      id,
      status,
      adminNotes,
    }: {
      id: string;
      status: CreatorStatus;
      adminNotes?: string | null;
    }) => {
      const patch: Record<string, unknown> = { status };
      if (adminNotes !== undefined) patch.admin_notes = adminNotes || null;
      const { error } = await supabase.from("creators" as any).update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, input }: { id: string; input: CreatorInput }) => {
      const { error } = await supabase.from("creators" as any).update(toRow(input)).eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  /** Fold a duplicate creator into a primary one and delete the duplicate. */
  const merge = useMutation({
    mutationFn: async ({ keepId, mergeId }: { keepId: string; mergeId: string }) => {
      if (keepId === mergeId) throw new Error("Pick two different creators.");
      const { error: relinkErr } = await supabase
        .from("discovery_tastemakers" as any)
        .update({ creator_id: keepId })
        .eq("creator_id", mergeId);
      if (relinkErr) throw relinkErr;
      const { error } = await supabase.from("creators" as any).delete().eq("id", mergeId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["tastemakers"] });
    },
  });

  return { setStatus, update, merge };
}

/* ------------------------------------------------------------------ */
/* Business intro requests (lead capture)                              */
/* ------------------------------------------------------------------ */

export interface IntroRequestInput {
  creatorId: string;
  businessName: string;
  contactEmail?: string;
  contactPhone?: string;
  emirate?: string;
  message?: string;
}

export function useCreateIntroRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: IntroRequestInput) => {
      const { data: userResp } = await supabase.auth.getUser();
      const uid = userResp.user?.id;
      if (!uid) throw new Error("Please sign in to request an intro.");
      const { error } = await supabase.from("creator_intro_requests" as any).insert({
        creator_id: input.creatorId,
        requested_by: uid,
        business_name: input.businessName.trim(),
        contact_email: input.contactEmail?.trim() || null,
        contact_phone: input.contactPhone?.trim() || null,
        emirate: input.emirate || null,
        message: input.message?.trim() || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["adminIntroRequests"] });
    },
  });
}

export function useAdminIntroRequests() {
  return useQuery({
    queryKey: ["adminIntroRequests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("creator_intro_requests" as any)
        .select("*, creator:creators(display_name, handle)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as any[];
    },
  });
}

export function useUpdateIntroRequestStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from("creator_intro_requests" as any)
        .update({ status })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["adminIntroRequests"] }),
  });
}

/* ------------------------------------------------------------------ */
/* Creator dashboard stats                                             */
/* ------------------------------------------------------------------ */

export interface CreatorFeature {
  stopId: string;
  stopName: string;
  goalId: string | null;
  status: string;
  socialLink: string | null;
  rejectionReason: string | null;
}

/** Every stop the signed-in user has submitted to, with review status. */
export function useMyFeaturedStops() {
  return useQuery({
    queryKey: ["myFeaturedStops"],
    refetchOnMount: "always",
    queryFn: async (): Promise<CreatorFeature[]> => {
      const { data: userResp } = await supabase.auth.getUser();
      const uid = userResp.user?.id;
      if (!uid) return [];
      const { data, error } = await supabase
        .from("discovery_stop_tastemakers" as any)
        .select("stop_id, status, social_link, rejection_reason, stop:discovery_stops(id, name, goal_id)")
        .eq("submitted_by", uid)
        .order("created_at", { ascending: false });
      if (error) return [];
      return (data || []).map((r: any) => ({
        stopId: r.stop_id,
        stopName: r.stop?.name ?? "Stop",
        goalId: r.stop?.goal_id ?? null,
        status: r.status ?? "pending",
        socialLink: r.social_link ?? null,
        rejectionReason: r.rejection_reason ?? null,
      }));
    },
  });
}

/* ------------------------------------------------------------------ */
/* Admin: preview any creator's hub + attach a record to an account    */
/* ------------------------------------------------------------------ */

export interface AdminCreatorHub extends Creator {
  ownerEmail: string | null;
}

/** Read one creator's record as an admin (used by the hub preview). */
export function useAdminCreatorHub(creatorId?: string | null) {
  return useQuery({
    queryKey: ["adminCreatorHub", creatorId],
    enabled: !!creatorId,
    staleTime: 30_000,
    queryFn: async (): Promise<AdminCreatorHub | null> => {
      const { data, error } = await supabase.rpc("admin_get_creator_hub" as any, {
        p_creator_id: creatorId,
      });
      if (error) throw error;
      const row = ((data as any[]) || [])[0];
      if (!row) return null;
      return { ...mapCreator(row), ownerEmail: row.owner_email ?? null };
    },
  });
}

/** Every stop submission belonging to that creator, as an admin. */
export function useAdminCreatorFeatures(creatorId?: string | null) {
  return useQuery({
    queryKey: ["adminCreatorFeatures", creatorId],
    enabled: !!creatorId,
    staleTime: 30_000,
    queryFn: async (): Promise<CreatorFeature[]> => {
      const { data, error } = await supabase.rpc("admin_get_creator_features" as any, {
        p_creator_id: creatorId,
      });
      if (error) throw error;
      return ((data as any[]) || []).map((r) => ({
        stopId: r.stop_id,
        stopName: r.stop_name ?? "Stop",
        goalId: r.goal_id ?? null,
        status: r.status ?? "pending",
        socialLink: r.social_link ?? null,
        rejectionReason: r.rejection_reason ?? null,
      }));
    },
  });
}

/** Attach a creator record to a real account so they can open their hub. */
export function useLinkCreatorToUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ creatorId, email }: { creatorId: string; email: string }) => {
      const { error } = await supabase.rpc("admin_link_creator_to_user" as any, {
        p_creator_id: creatorId,
        p_email: email.trim(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["adminCreators"] });
      qc.invalidateQueries({ queryKey: ["myCreatorProfile"] });
    },
  });
}

/* ------------------------------------------------------------------ */
/* VIP invite links — claim a pre-seeded creator profile               */
/* ------------------------------------------------------------------ */

/** Admin: generate (or regenerate) a one-time VIP claim token. */
export function useGenerateVipInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (creatorId: string): Promise<string> => {
      const { data, error } = await supabase.rpc("admin_generate_creator_vip_token" as any, {
        p_creator_id: creatorId,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["adminCreators"] });
    },
  });
}

export interface CreatorClaimPreview {
  id: string;
  displayName: string;
  handle: string | null;
  avatarUrl: string | null;
  bio: string | null;
  emirates: string[];
  categories: string[];
  featuredStops: number;
}

/** Public: preview the reserved profile behind a VIP claim token. */
export function useCreatorClaimPreview(token?: string | null) {
  return useQuery({
    queryKey: ["creatorClaimPreview", token],
    enabled: !!token,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<CreatorClaimPreview | null> => {
      const { data, error } = await supabase.rpc("get_creator_claim_preview" as any, {
        p_token: token,
      });
      if (error) throw error;
      const row = ((data as any[]) || [])[0];
      if (!row) return null;
      return {
        id: row.id,
        displayName: row.display_name,
        handle: row.handle ?? null,
        avatarUrl: row.avatar_url ?? null,
        bio: row.bio ?? null,
        emirates: row.emirates ?? [],
        categories: row.categories ?? [],
        featuredStops: Number(row.featured_stops ?? 0),
      };
    },
  });
}

/** Signed-in user: claim the reserved profile behind a VIP token. */
export function useClaimCreatorProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (token: string): Promise<string> => {
      const { data, error } = await supabase.rpc("claim_creator_profile" as any, {
        p_token: token,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["myCreatorProfile"] });
      qc.invalidateQueries({ queryKey: ["adminCreators"] });
      qc.invalidateQueries({ queryKey: ["publicCreators"] });
      qc.invalidateQueries({ queryKey: ["creatorClaimPreview"] });
    },
  });
}

/** Join form: detect a pre-seeded, unclaimed profile for a typed handle. */
export function useUnclaimedCreatorByHandle(handle: string) {
  const cleaned = handle.trim().replace(/^@/, "");
  return useQuery({
    queryKey: ["unclaimedCreatorHandle", cleaned.toLowerCase()],
    enabled: cleaned.length >= 3,
    staleTime: 30_000,
    queryFn: async (): Promise<{ id: string; displayName: string; handle: string | null } | null> => {
      const { data, error } = await supabase.rpc("get_unclaimed_creator_by_handle" as any, {
        p_handle: cleaned,
      });
      if (error) return null;
      const row = ((data as any[]) || [])[0];
      if (!row) return null;
      return { id: row.id, displayName: row.display_name, handle: row.handle ?? null };
    },
  });
}
